import { Box, IconButton, Typography } from "@mui/material"
import {
  CheckCircleFilledOutlined,
  DangerousFilledOutlined,
  DeleteOutlined,
  UploadFileOutlined,
  VisibilityOutlined,
} from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import { useCallback, useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"

import { FileTypeIcon } from "./FileTypeIcon"
import { FileUpload } from "./FileUpload"
import { formatFileSize } from "./utils"

import type { File as AerosFile, SignedUrl } from "@/graphql/codegen/graphql"
import { useDragAndDrop } from "@/hooks/useDragAndDrop"

interface SingleFileUploadProps {
  onChange: (file: File | null) => void
  fileUrl?: string | null
  file?: File | SignedUrl | null
  accept?: string
  title?: string
  subtitle?: string
  dropTitle?: string
  readOnly?: boolean
  error?: boolean
  "data-testid"?: string
}

const EmptyUploadContent = ({ title, subtitle }: { title: string; subtitle: string }): ReactElement => (
  <>
    <UploadFileOutlined className='text-primary' />
    <Typography variant='subtitle1' className='font-normal text-gray-900'>
      <Box component='span' className='text-primary underline'>
        {title}
      </Box>{" "}
      or drag and drop
    </Typography>
    <Typography variant='body2' color='textSecondary'>
      {subtitle}
    </Typography>
  </>
)
// todo move to shared
export const FilePreview = ({
  file,
  readOnly = false,
  onRemove,
  error,
  "data-testid": testId,
}: {
  file: File | AerosFile | null
  readOnly?: boolean
  onRemove?: () => void
  error?: boolean
  "data-testid"?: string
}): ReactElement => {
  const iconFileName = (file ? ("name" in file ? file.name : file.label) : "") ?? ""
  const fileName = file
    ? "name" in file
      ? file.name
      : "originalName" in file
        ? file.originalName
        : file.label
    : undefined

  return (
    <Box className='relative flex size-full items-center justify-between'>
      <Box className='flex w-full items-center gap-4 p-4'>
        <Box className='flex min-w-0 flex-1 items-center gap-4'>
          <FileTypeIcon fileName={iconFileName} className='size-12' />
          <Box className='flex min-w-0 flex-1 flex-col'>
            <Typography variant='body2' className='truncate font-medium text-gray-900'>
              {fileName || "Untitled"}
            </Typography>
            <Typography variant='caption' className='flex items-center gap-2 text-gray-500'>
              {file && "size" in file && file.size && <>{formatFileSize(file.size)} &middot; Completed</>}
            </Typography>
          </Box>
        </Box>

        <Box className='flex items-center gap-4'>
          {!readOnly ? (
            <>
              <IconButton
                size='small'
                className='text-gray-400'
                onClick={(e) => {
                  e?.preventDefault()
                  e?.stopPropagation()
                  onRemove?.()
                }}
                data-testid={`${testId}-delete-button`}
              >
                <DeleteOutlined fontSize='medium' className='text-gray-900' />
              </IconButton>
              {error ? (
                <DangerousFilledOutlined className='text-[32px] text-red-600' />
              ) : (
                <CheckCircleFilledOutlined className='text-[32px] text-green-700' />
              )}
            </>
          ) : (
            <IconButton size='small' className='text-gray-400' data-testid={`${testId}-view-button`}>
              <VisibilityOutlined fontSize='medium' className='text-gray-900' />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  )
}

const DragOverlay = ({
  isOver,
  canDrop,
  dropTitle,
}: {
  isOver: boolean
  canDrop: boolean
  dropTitle: string
}): ReactElement => (
  <Box
    className={`pointer-events-none absolute inset-0 z-10 flex size-full items-center justify-center rounded border-none transition-all duration-200 ${
      isOver && !canDrop ? "bg-white/90" : "bg-transparent"
    }`}
  >
    <Box
      className={`flex flex-col items-center gap-2 transition-opacity duration-200 ${
        isOver && !canDrop ? "opacity-100" : "opacity-0"
      }`}
    >
      <UploadFileOutlined className='text-4xl text-primary' />
      <Typography variant='body1' className='text-primary'>
        {dropTitle}
      </Typography>
    </Box>
  </Box>
)

export const SingleFileUpload = ({
  onChange,
  fileUrl,
  file,
  accept = ".jpg,.png,.gif,.jpeg",
  title = "Click to upload",
  subtitle = "JPG, PNG, GIF, JPEG (max. 10MB)",
  dropTitle = "Drop file here",
  readOnly = false,
  error,
  "data-testid": testId = "file-upload",
}: SingleFileUploadProps): ReactElement => {
  const isSignedUrl = file && "url" in file
  const [previewUrl, setPreviewUrl] = useState<string | null>(fileUrl || null)
  const [currentFile, setCurrentFile] = useState<File | AerosFile | null>(
    isSignedUrl ? (file.file as AerosFile) : file || null
  )

  const handleFileChange = useCallback(
    (file: File) => {
      const tempUrl = URL.createObjectURL(file)
      setPreviewUrl(tempUrl)
      setCurrentFile(file)
      onChange(file)
    },
    [onChange]
  )

  const { dropRef, isOver, canDrop } = useDragAndDrop({
    onDrop: handleFileChange,
    enabled: true,
  })

  useEffect(() => {
    if (fileUrl) {
      setPreviewUrl(fileUrl)
    }
  }, [fileUrl])

  useEffect(() => {
    if (file) {
      setCurrentFile(isSignedUrl ? (file.file as AerosFile) : file || null)
    }
  }, [file, isSignedUrl])

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl !== fileUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl, fileUrl])

  const onRemove = () => {
    setPreviewUrl(null)
    setCurrentFile(null)
    onChange(null)
  }

  return (
    <FileUpload
      onChange={handleFileChange}
      accept={accept}
      fileUrl={previewUrl}
      file={currentFile}
      enableDragAndDrop={true}
      readOnly={readOnly}
      data-testid={testId}
      templates={{
        content: () => (
          <Box
            ref={dropRef}
            className={twMerge(
              "flex w-full flex-col items-center justify-center gap-2",
              readOnly && "pointer-events-none",
              !currentFile ? "px-4 py-6" : ""
            )}
            data-testid={`${testId}-drop-area`}
          >
            {!currentFile ? (
              <EmptyUploadContent title={title} subtitle={subtitle} />
            ) : (
              <FilePreview file={currentFile} onRemove={onRemove} error={error} data-testid={`${testId}-preview`} />
            )}
          </Box>
        ),
        overlay: () => <DragOverlay isOver={isOver} canDrop={canDrop} dropTitle={dropTitle} />,
        hover: () => null,
      }}
    />
  )
}
